import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, WS_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSocial } from "@/lib/social";
import { usePartyVoice } from "@/lib/usePartyVoice";
import GameSlot from "@/components/games/GameSlot";
import { Users, Radio, Gamepad2, Lock, Plus, Copy, Check, LogOut, Send, UserPlus, Mic, MicOff, Headphones, Volume2, VolumeX } from "lucide-react";

const TYPE_META = {
  watch: { label: "Watch Party", icon: Radio, color: "#00F0FF" },
  game: { label: "Game Party", icon: Gamepad2, color: "#CCFF00" },
  private: { label: "Private Room", icon: Lock, color: "#FF5900" },
};

export function PartiesList() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [parties, setParties] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", type: "game", watching: "" });
  const [joinCode, setJoinCode] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => { if (!user) nav("/login"); }, [user, nav]);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = () => api.get("/parties/my").then(r => setParties(r.data));

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      const r = await api.post("/parties", { ...form, watching: form.watching || null });
      setCreating(false);
      setForm({ name: "", type: "game", watching: "" });
      nav(`/parties/${r.data.id}`);
    } catch (e2) { setErr(e2?.response?.data?.detail || "Failed"); }
  };
  const join = async (e) => {
    e.preventDefault();
    if (!joinCode.trim()) return;
    try {
      const r = await api.post("/parties/join", { invite_code: joinCode.trim() });
      nav(`/parties/${r.data.party_id}`);
    } catch (e2) { setErr(e2?.response?.data?.detail || "Invalid code"); }
  };

  if (!user) return null;

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
      <div className="sc-label" style={{ color: "var(--sc-cyan)" }}>Squad Up</div>
      <h1 className="sc-heading text-4xl font-black tracking-tight mt-1 mb-8">Parties &amp; Rooms</h1>

      <div className="grid lg:grid-cols-2 gap-4 mb-8">
        {/* Create */}
        <div className="sc-card p-6">
          <div className="sc-heading text-lg font-bold mb-3">Create a Party</div>
          <form onSubmit={create} className="space-y-3">
            <input data-testid="party-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Friday Poker Night" className="sc-input text-sm" required />
            <select data-testid="party-type" value={form.type} onChange={e => setForm({...form, type: e.target.value})} className="sc-input text-sm">
              <option value="game">Game Party — play games with friends</option>
              <option value="watch">Watch Party — watch a stream together</option>
              <option value="private">Private Room — invite-only</option>
            </select>
            {form.type === "watch" && (
              <input value={form.watching} onChange={e => setForm({...form, watching: e.target.value})} placeholder="Streamer username to watch" className="sc-input text-sm" />
            )}
            <button data-testid="party-create-submit" className="sc-btn text-xs w-full flex items-center justify-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Create</button>
          </form>
        </div>
        {/* Join */}
        <div className="sc-card p-6">
          <div className="sc-heading text-lg font-bold mb-3">Join with a Code</div>
          <form onSubmit={join} className="space-y-3">
            <input data-testid="party-join-code" value={joinCode} onChange={e => setJoinCode(e.target.value.toUpperCase())} placeholder="8-character invite code" maxLength={8} className="sc-input text-sm sc-mono tracking-widest" />
            <button data-testid="party-join-submit" className="sc-btn-volt sc-btn text-xs w-full">Join Party</button>
          </form>
          {err && <div className="text-[#FF1053] text-xs mt-2">{err}</div>}
        </div>
      </div>

      <div className="sc-label mb-3">Your Parties ({parties.length})</div>
      {parties.length === 0 && (
        <div className="border border-dashed border-white/10 rounded-md p-10 text-center">
          <Users className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
          <div className="text-sm text-zinc-300">No parties yet. Create one above or join with a code from a friend.</div>
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {parties.map(p => {
          const meta = TYPE_META[p.type] || TYPE_META.game;
          return (
            <Link key={p.id} to={`/parties/${p.id}`} data-testid={`party-row-${p.id}`} className="sc-card p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="sc-label" style={{ color: meta.color }}>{meta.label}</div>
                <meta.icon className="w-4 h-4" style={{ color: meta.color }} />
              </div>
              <div className="sc-heading text-lg font-bold">{p.name}</div>
              <div className="text-xs text-zinc-500 mt-1">Host: {p.host_username} · {p.member_ids.length} member{p.member_ids.length === 1 ? "" : "s"}</div>
              <div className="text-[10px] sc-mono text-zinc-400 mt-3 tracking-widest">{p.invite_code}</div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export function PartyRoom() {
  const { partyId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const social = useSocial();
  const [party, setParty] = useState(null);
  const [ws, setWs] = useState(null);
  const [messages, setMessages] = useState([]);
  const [partyState, setPartyState] = useState({ presence: [], count: 0, votes: {} });
  const [gameState, setGameState] = useState({});
  const [feed, setFeed] = useState([]);
  const [text, setText] = useState("");
  const [copied, setCopied] = useState(false);
  const [inviteUser, setInviteUser] = useState("");
  const [notice, setNotice] = useState("");
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [myPeerId, setMyPeerId] = useState(null);
  const listRef = useRef(null);

  const voice = usePartyVoice(ws, myPeerId);

  useEffect(() => { if (!user) nav("/login"); }, [user, nav]);

  useEffect(() => {
    if (!user || !partyId) return;
    api.get(`/parties/${partyId}`).then(r => setParty(r.data)).catch(() => nav("/parties"));
    const token = localStorage.getItem("sc_token");
    const sock = new WebSocket(`${WS_BASE}/api/ws/party/${partyId}?token=${encodeURIComponent(token)}`);
    sock.onmessage = (e) => {
      const m = JSON.parse(e.data);
      if (m.type === "chat") setMessages(prev => [...prev.slice(-199), m.message]);
      else if (m.type === "party_state") setPartyState(m.state || { presence: [], count: 0, votes: {}, voice: [] });
      else if (m.type === "game_state") setGameState(m.state || {});
      else if (m.type === "feed") setFeed(m.feed || []);
      else if (m.type === "welcome") {
        if (m.peer_id) setMyPeerId(m.peer_id);
        if (m.feed) setFeed(m.feed);
      }
    };
    setWs(sock);
    return () => sock.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyId, user?.username]);

  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [messages]);

  if (!user || !party) return <div className="p-10 text-center text-zinc-400">Loading party...</div>;

  const meta = TYPE_META[party.type] || TYPE_META.game;
  const isHost = party.host_user_id === user.id;
  const send = (msg) => { if (!ws || ws.readyState !== 1) return; ws.send(JSON.stringify(msg)); };
  const sendChat = () => { const t = text.trim(); if (!t) return; send({ type: "chat", text: t }); setText(""); };
  const copyCode = () => { navigator.clipboard.writeText(party.invite_code); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  const shareLink = () => {
    const url = `${window.location.origin}/parties?join=${party.invite_code}`;
    navigator.clipboard.writeText(url);
    setNotice("Share link copied"); setTimeout(() => setNotice(""), 1500);
  };
  const doInvite = async (e) => {
    e.preventDefault(); if (!inviteUser.trim()) return;
    try { await api.post(`/parties/${party.id}/invite`, { username: inviteUser.trim() }); setInviteUser(""); setNotice("Invited!"); setTimeout(() => setNotice(""), 1500); }
    catch (e2) { setNotice(e2?.response?.data?.detail || "Failed"); }
  };
  const leave = async () => {
    if (!window.confirm(isHost ? "Leaving as host dissolves the party. Continue?" : "Leave this party?")) return;
    await api.post(`/parties/${party.id}/leave`); nav("/parties");
  };

  const me = partyState.presence.find(p => p.username === user.username);
  const iAmReady = !!me?.ready;
  const readyCount = partyState.presence.filter(p => p.ready).length;
  const allReady = partyState.presence.length >= 2 && readyCount === partyState.presence.length;

  // Voting tally
  const voteCounts = {};
  Object.values(partyState.votes || {}).forEach(slug => { voteCounts[slug] = (voteCounts[slug] || 0) + 1; });
  const totalVotes = Object.values(voteCounts).reduce((a,b) => a+b, 0) || 1;
  const games = [
    { slug: "holdem", label: "Texas Hold'em" }, { slug: "chess", label: "Chess" },
    { slug: "connect4", label: "Connect 4" }, { slug: "checkers", label: "Checkers" },
    { slug: "hangman", label: "Hangman" }, { slug: "slots", label: "Slots" },
    { slug: "coinflip", label: "Coin Flip" },
  ];
  const myVote = partyState.votes?.[Object.keys(partyState.votes || {}).find(k => partyState.presence.find(p => p.peer_id === k)?.username === user.username)] || null;

  const visibleMsgs = messages.filter(m => !social.isBlocked(m.username) && !social.isMuted(m.username));

  // Mock XP data (persisted server-side later)
  const xp = party.xp || 340;
  const level = party.level || 3;
  const xpPct = Math.min(100, (xp % 500) / 5);

  const emojis = ["😂","🔥","🎉","💎","♠","♥","🎰","🃏","👑","🎮","🍕","🚀","💯","🥂","⚡","👀"];

  return (
    <div className="max-w-[1600px] mx-auto px-4 lg:px-8 py-6 grid grid-cols-12 gap-4">
      {/* LEFT: Header + Stats + Widgets */}
      <div className="col-span-12 lg:col-span-8 space-y-4">
        {/* HEADER */}
        <div className="sc-card p-6 relative overflow-hidden">
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full blur-3xl opacity-20" style={{ background: meta.color }} />
          <div className="flex items-start justify-between flex-wrap gap-3 relative">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <meta.icon className="w-4 h-4" style={{ color: meta.color }} />
                <div className="sc-label" style={{ color: meta.color }}>{meta.label}</div>
                <span className="text-[9px] tracking-widest uppercase font-bold px-2 py-0.5 rounded-sm sc-mono" style={{ background: party.type === "private" ? "var(--sc-orange)" : "var(--sc-volt)", color: "#0A0A0C" }}>
                  {party.type === "private" ? "PRIVATE" : "PUBLIC"}
                </span>
              </div>
              <h1 className="sc-heading text-4xl font-black tracking-tight">{party.name}</h1>
              {party.description && <p className="text-sm text-zinc-400 mt-2 max-w-xl">{party.description}</p>}
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                <div className="flex items-center gap-1.5 text-xs text-zinc-400">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold" style={{ background: "linear-gradient(135deg,#FF1053,#FF5900)" }}>{party.host_username[0].toUpperCase()}</div>
                  <span>Host: <span className="text-white font-semibold">{party.host_username}</span></span>
                </div>
                <div className="text-xs text-zinc-500 sc-mono">{partyState.count} / 8 members</div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-2">
                <div className="coin-pill" data-testid="party-invite-code">
                  {party.invite_code}
                  <button onClick={copyCode} className="ml-1" title="Copy code">{copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}</button>
                </div>
                <button onClick={shareLink} className="sc-btn-ghost text-xs">Share</button>
              </div>
              <button data-testid="party-leave" onClick={leave} className="sc-btn-ghost text-xs flex items-center gap-1"><LogOut className="w-3 h-3" /> {isHost ? "Dissolve" : "Leave"}</button>
            </div>
          </div>
          {/* XP Bar */}
          <div className="mt-6 relative">
            <div className="flex items-center justify-between text-xs mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-md flex items-center justify-center sc-heading font-black text-sm" style={{ background: "linear-gradient(135deg,#CCFF00,#FF5900)", color: "#0A0A0C" }}>{level}</div>
                <div><div className="sc-label" style={{ color: "var(--sc-volt)" }}>Party Level</div><div className="text-white font-bold text-sm">{xp} XP</div></div>
              </div>
              <div className="text-[10px] sc-mono text-zinc-500">{500 - (xp%500)} XP to Lv {level+1}</div>
            </div>
            <div className="h-2 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full transition-all duration-500" style={{ width: `${xpPct}%`, background: "linear-gradient(90deg, #CCFF00, #FF5900, #FF1053)", boxShadow: "0 0 12px rgba(204,255,0,0.5)" }} />
            </div>
          </div>
          {isHost && (
            <form onSubmit={doInvite} className="flex gap-2 mt-4">
              <input data-testid="party-invite-username" value={inviteUser} onChange={e => setInviteUser(e.target.value)} placeholder="Invite friend by username" className="sc-input text-sm max-w-xs" />
              <button data-testid="party-invite-submit" className="sc-btn text-xs"><UserPlus className="w-3 h-3 inline mr-1" />Invite</button>
              {notice && <span className="text-xs text-[color:var(--sc-volt)] self-center">{notice}</span>}
            </form>
          )}
        </div>

        {/* READY + LAUNCH */}
        <div className="sc-card p-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="sc-label" style={{ color: "var(--sc-volt)" }}>Ready-up</div>
              <div className="text-sm text-zinc-400 mt-1"><span className="text-white font-bold sc-mono">{readyCount} / {partyState.count}</span> ready</div>
            </div>
            <button
              data-testid="party-ready-toggle"
              onClick={() => send({ type: "ready_toggle" })}
              className={`px-6 py-3 rounded-full text-sm font-black sc-mono tracking-widest transition ${iAmReady ? "bg-[color:var(--sc-volt)] text-black shadow-[0_0_24px_rgba(204,255,0,0.4)]" : "border border-white/10 text-zinc-300 hover:border-[color:var(--sc-volt)]"}`}
              style={iAmReady ? { animation: "pulse-volt 1.6s infinite" } : {}}
            >
              {iAmReady ? "✅ READY" : "READY UP"}
            </button>
          </div>
          {allReady && (
            <button className="w-full mt-3 py-4 rounded-md sc-heading text-xl font-black tracking-widest text-black" style={{ background: "linear-gradient(90deg, #CCFF00, #00F0FF)", animation: "pulse-volt 1.6s infinite" }}>
              🚀 LAUNCH GAME
            </button>
          )}
        </div>

        {/* VOTING */}
        <div className="sc-card p-6">
          <div className="sc-label mb-3" style={{ color: "var(--sc-cyan)" }}>Vote — Next Game</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {games.map(g => {
              const count = voteCounts[g.slug] || 0;
              const pct = totalVotes ? Math.round((count / totalVotes) * 100) : 0;
              const isMine = myVote === g.slug;
              return (
                <button
                  key={g.slug}
                  data-testid={`party-vote-${g.slug}`}
                  onClick={() => send({ type: "vote", slug: g.slug })}
                  className={`relative p-3 border rounded-sm text-left overflow-hidden transition ${isMine ? "border-[color:var(--sc-pink)]" : "border-white/10 hover:border-white/25"}`}
                >
                  <div className="absolute inset-0 opacity-20" style={{ background: `linear-gradient(90deg, var(--sc-pink) ${pct}%, transparent ${pct}%)` }} />
                  <div className="relative flex items-center justify-between">
                    <div className="text-sm font-semibold">{g.label}</div>
                    <div className="text-xs sc-mono text-zinc-400">{pct}%</div>
                  </div>
                  <div className="relative text-[10px] text-zinc-500 mt-0.5">{count} vote{count === 1 ? "" : "s"}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* STATS + WIDGETS */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[
            { label: "Games Played", value: party.stats?.games_played || 0, color: "#00F0FF" },
            { label: "Games Won", value: party.stats?.games_won || 0, color: "#CCFF00" },
            { label: "Win Streak", value: party.stats?.streak || 0, color: "#FF5900" },
            { label: "Credits Earned", value: party.stats?.credits || 0, color: "#F59E0B" },
            { label: "Tournament Wins", value: party.stats?.tournaments_won || 0, color: "#FF1053" },
            { label: "Favorite Game", value: "Hold'em", color: "#A78BFA" },
          ].map(s => (
            <div key={s.label} className="sc-card p-4">
              <div className="sc-label" style={{ color: s.color }}>{s.label}</div>
              <div className="sc-heading text-2xl font-black mt-1">{s.value}</div>
            </div>
          ))}
        </div>

        {/* ACHIEVEMENTS */}
        <div className="sc-card p-6">
          <div className="sc-label mb-3" style={{ color: "var(--sc-orange)" }}>Party Achievements</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { name: "Game Night", earned: true, icon: "🎮" },
              { name: "First Win", earned: true, icon: "🏆" },
              { name: "Perfect Party", earned: false, icon: "✨" },
              { name: "Tournament Champ", earned: false, icon: "👑" },
            ].map(a => (
              <div key={a.name} className={`p-3 border rounded-sm text-center transition ${a.earned ? "border-[color:var(--sc-volt)]/40 bg-[color:var(--sc-volt)]/5" : "border-white/5 opacity-40"}`}>
                <div className="text-2xl">{a.icon}</div>
                <div className="text-[10px] sc-mono font-bold mt-1 tracking-wider">{a.name}</div>
                {a.earned && <div className="text-[9px] text-[color:var(--sc-volt)] mt-0.5">UNLOCKED</div>}
              </div>
            ))}
          </div>
        </div>

        {/* GAME AREA */}
        <GameSlot ws={ws} state={gameState} isStreamer={isHost} channelUsername={`party:${party.id}`} />
      </div>

      {/* RIGHT: Members + Chat + Voice + Feed */}
      <aside className="col-span-12 lg:col-span-4 space-y-3">
        {/* Voice Chat */}
        <div className="sc-card p-4" data-testid="party-voice-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="sc-label" style={{ color: "var(--sc-cyan)" }}>Voice Chat</div>
            <span
              className={`text-[9px] tracking-widest uppercase font-bold px-2 py-0.5 rounded-sm sc-mono ${voice.enabled ? "bg-[color:var(--sc-volt)] text-black" : "bg-white/5 text-zinc-500"}`}
              data-testid="voice-status"
            >
              {voice.enabled ? "CONNECTED" : "OFF"}
            </span>
          </div>
          <div className="flex gap-2">
            {!voice.enabled ? (
              <button
                onClick={voice.join}
                className="flex-1 py-2 rounded-md text-xs font-bold sc-mono sc-btn flex items-center justify-center gap-1.5"
                data-testid="voice-join-btn"
              >
                <Mic className="w-3.5 h-3.5" /> JOIN VOICE
              </button>
            ) : (
              <>
                <button
                  onClick={voice.leave}
                  className="flex-1 py-2 rounded-md text-xs font-bold sc-mono bg-[color:var(--sc-pink)] text-white hover:brightness-110 flex items-center justify-center gap-1.5"
                  data-testid="voice-leave-btn"
                >
                  <LogOut className="w-3.5 h-3.5" /> LEAVE
                </button>
                <button
                  onClick={voice.toggleMute}
                  className={`p-2 rounded-md border text-xs ${voice.muted ? "bg-[color:var(--sc-pink)]/20 border-[color:var(--sc-pink)]/60 text-[color:var(--sc-pink)]" : "border-white/10 hover:border-white/20"}`}
                  title={voice.muted ? "Unmute" : "Mute"}
                  data-testid="voice-mute-btn"
                >
                  {voice.muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </button>
                <button
                  onClick={voice.toggleDeafen}
                  className={`p-2 rounded-md border text-xs ${voice.deafened ? "bg-[color:var(--sc-orange)]/20 border-[color:var(--sc-orange)]/60 text-[color:var(--sc-orange)]" : "border-white/10 hover:border-white/20"}`}
                  title={voice.deafened ? "Undeafen" : "Deafen"}
                  data-testid="voice-deafen-btn"
                >
                  {voice.deafened ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
              </>
            )}
          </div>
          {(partyState.voice && partyState.voice.length > 0) ? (
            <div className="mt-3 pt-3 border-t border-white/5 space-y-1.5" data-testid="voice-roster">
              {partyState.voice.map((v) => {
                const speaking = voice.speakingPeers[v.peer_id];
                const isMe = v.peer_id === myPeerId;
                return (
                  <div key={v.peer_id} className="flex items-center gap-2 text-xs" data-testid={`voice-peer-${v.username}`}>
                    <div
                      className={`w-2 h-2 rounded-full transition ${speaking ? "bg-[color:var(--sc-volt)]" : "bg-white/20"}`}
                      style={speaking ? { boxShadow: "0 0 8px var(--sc-volt)" } : undefined}
                    />
                    <Headphones className="w-3 h-3 text-zinc-500" />
                    <span className="text-zinc-300 truncate flex-1">{v.username}{isMe ? " (you)" : ""}</span>
                    {v.muted && <MicOff className="w-3 h-3 text-[color:var(--sc-pink)]" />}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="mt-3 pt-3 border-t border-white/5 text-[10px] text-zinc-500 sc-mono">
              {voice.enabled ? "waiting for others to join…" : "nobody in voice yet"}
            </div>
          )}
        </div>

        {/* Members */}
        <div className="sc-card p-4">
          <div className="sc-label mb-3">Members ({partyState.count})</div>
          <div className="space-y-2 max-h-[240px] overflow-y-auto">
            {partyState.presence.map(p => (
              <div key={p.peer_id} className="flex items-center gap-2">
                <div className="relative">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center sc-heading font-bold text-xs" style={{ background: "linear-gradient(135deg,#FF1053,#FF5900)" }}>{p.username[0].toUpperCase()}</div>
                  <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-[#18181C] bg-[color:var(--sc-volt)]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold flex items-center gap-1.5">
                    {p.username}
                    {p.username === party.host_username && <span className="text-[9px] tracking-widest uppercase font-bold px-1 py-0.5 rounded-sm bg-[color:var(--sc-cyan)] text-black">HOST</span>}
                  </div>
                  <div className="text-[10px] text-zinc-500">{p.ready ? "✅ Ready" : "🟢 Online"}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Chat */}
        <div className="sc-card p-0 flex flex-col h-[300px]">
          <div className="px-4 py-3 border-b border-white/10 sc-label">Party Chat</div>
          <div ref={listRef} className="flex-1 overflow-y-auto py-2">
            {visibleMsgs.length === 0 && <div className="text-xs text-zinc-500 px-4 py-3">Say hi to the squad.</div>}
            {visibleMsgs.map(m => (
              <div key={m.id} className="chat-msg">
                <span className="font-bold mr-1.5" style={{ color: m.color }}>{m.username}:</span>
                <span className="text-zinc-200">{m.text}</span>
              </div>
            ))}
          </div>
          {emojiOpen && (
            <div className="px-2 py-2 border-t border-white/10 flex flex-wrap gap-1">
              {emojis.map(e => <button key={e} onClick={() => { setText(t => t + e); setEmojiOpen(false); }} className="w-7 h-7 text-lg hover:bg-white/10 rounded-sm">{e}</button>)}
            </div>
          )}
          <div className="p-2 border-t border-white/10 flex gap-1">
            <button onClick={() => setEmojiOpen(o => !o)} className="px-2 border border-white/10 rounded-md text-sm">😊</button>
            <input data-testid="party-chat-input" value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === "Enter" && sendChat()} className="sc-input" placeholder="Message the party" />
            <button data-testid="party-chat-send" onClick={sendChat} className="sc-btn"><Send className="w-3.5 h-3.5" /></button>
          </div>
        </div>

        {/* Feed */}
        <div className="sc-card p-4 max-h-[280px] overflow-y-auto">
          <div className="sc-label mb-3">Party Feed</div>
          {feed.length === 0 && <div className="text-xs text-zinc-500">No activity yet.</div>}
          <div className="space-y-1.5">
            {[...feed].reverse().slice(0, 20).map(f => (
              <div key={f.id} className="text-[11px] text-zinc-400 flex items-start gap-1.5">
                <span className="text-[color:var(--sc-cyan)]">›</span>
                <span>{f.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Daily challenges */}
        <div className="sc-card p-4">
          <div className="sc-label mb-3" style={{ color: "var(--sc-pink)" }}>Daily Challenges</div>
          {[
            { name: "Play 3 games together", progress: 60, reward: "+50 credits" },
            { name: "Win 5 matches", progress: 20, reward: "+100 XP" },
            { name: "Watch a stream 30m", progress: 0, reward: "+25 credits" },
          ].map(c => (
            <div key={c.name} className="mb-3">
              <div className="flex items-center justify-between text-xs mb-1"><span>{c.name}</span><span className="sc-mono text-[color:var(--sc-volt)]">{c.reward}</span></div>
              <div className="h-1.5 bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-[color:var(--sc-pink)] to-[color:var(--sc-volt)]" style={{ width: `${c.progress}%` }} /></div>
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
}
